/** Render stored tour HTML read-only, sanitised. The console lints
 *  `react/no-danger` as an error, so all HTML must pass through DOMPurify
 *  first — this is the one vetted place that does. */
import { Box } from '@mui/material';
import DOMPurify from 'dompurify';
import { useMemo } from 'react';
import type { SxProps, Theme } from '@mui/material';

const ALLOWED_TAGS = ['p', 'br', 'b', 'strong', 'i', 'em', 'u', 'ul', 'ol', 'li', 'a', 'span'];
const ALLOWED_ATTR = ['href', 'target', 'rel'];

// Force every surviving link to open safely in a new tab (stored HTML rarely sets
// target/rel itself). Registered once at module load.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node instanceof Element && node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

export function HtmlContent({ html, sx }: { html: string | null | undefined; sx?: SxProps<Theme> }) {
  const clean = useMemo(
    () => DOMPurify.sanitize(html ?? '', { ALLOWED_TAGS, ALLOWED_ATTR }),
    [html],
  );
  if (!clean.trim()) return null;
  return (
    <Box
      sx={{ '& p': { m: 0, mb: 0.5 }, '& a': { color: 'primary.main' }, fontSize: 14, ...sx }}
      // eslint-disable-next-line react/no-danger -- sanitised above via DOMPurify
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}
